import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { OrgSelectorService } from 'src/app/core/services/org-selector.service';

import { Card } from '../../../../core/models/card.model';
import { isSuperAdmin } from '../../../../core/utils/is-super-admin.util';
import { AuthenticationsService } from '../../../../modules/authentications/authentications.service';
import { RouteUtils } from '../../../../shared/utils/route/route.util';
import { AppState } from '../../../../store/app.state';
import {
	fetchOrgHubClear,
	fetchOrgHubStart,
} from '../../state/organizations.actions';
import { selectOrghub } from '../../state/organizations.selector';

const CLAIM_USAGE_INGESTION_CARD: Card = {
	color: 'purple',
	description: 'Upload de arquivos de utilização de sinistro',
	icon: 'sanus-upload',
	title: 'Upload de sinistros',
	path: '/ingestao-de-sinistros',
};

const CONTRACTS_CARD: Card = {
	color: 'teal',
	description: 'Gerenciamento dos contratos da empresa',
	icon: 'sanus-policy',
	title: 'Contratos',
	path: '/contratos',
};

const withContractsCard = (items: Card[]): Card[] => {
	const hasContractsCard = items.some(
		(item) => item.path === CONTRACTS_CARD.path,
	);

	return hasContractsCard ? [...items] : [...items, CONTRACTS_CARD];
};

@Component({
	selector: 'app-org-hub',
	styleUrls: ['./org-hub.component.scss'],
	templateUrl: './org-hub.component.html',
})
export class OrgHubComponent implements OnInit, OnDestroy {
	public card: Card | null = null;

	public cards: Card[] = [];
	private orgHubItems: Card[] = [];
	private paramsId: string = '';

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly authenticationsService: AuthenticationsService,
		private readonly store: Store<AppState>,
		private readonly orgSelection: OrgSelectorService,
	) {}

	get organizationName(): string {
		return this.orgSelection.getOrganizationName() ?? '';
	}

	clickAction(path: string): void {
		switch (path) {
			case '/grupos':
				void this.router.navigateByUrl(`grupos`);
				break;
			case '/apolices':
				void this.router.navigateByUrl(
					`organizacoes/${this.paramsId}${path}?type=saude`,
				);
				break;
			default:
				void this.router.navigateByUrl(`organizacoes/${this.paramsId}${path}`);
		}
	}

	isSuperUser(): boolean {
		return isSuperAdmin(this.authenticationsService.getSession());
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(fetchOrgHubClear());
	}

	ngOnInit(): void {
		this.activatedRoute.paramMap.subscribe((params) => {
			const organizationId = RouteUtils.getRequiredParam({
				param: RouteUtils.params.organizationId,
				params,
			});
			const selectedId = this.orgSelection.getOrganizationId();
			this.paramsId = organizationId;
			if (organizationId !== selectedId) {
				localStorage.setItem('org-id', organizationId);
				localStorage.setItem(
					'org-name',
					this.orgSelection.getOrganizationName(),
				);
				this.orgSelection.setOrganizationId(organizationId);
			}
			this.store
				.select(selectOrghub)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((hubs) => {
					if (hubs) {
						this.orgHubItems = hubs.map((hub) => hub);
						this.updateCards();
					}
				});
			this.store.dispatch(fetchOrgHubStart({ id: organizationId }));
		});
	}

	private updateCards(): void {
		if (this.orgHubItems.length > 0) {
			const items = this.withClaimUsageIngestionCard(
				withContractsCard(this.orgHubItems),
			);

			if (items[0].path === '/cadastro') {
				this.card = items[0];
				this.cards = items.slice(1);
			} else {
				this.card = null;
				this.cards = items;
			}
		}
	}

	private withClaimUsageIngestionCard(items: Card[]): Card[] {
		const hasIngestionCard = items.some(
			(item) => item.path === CLAIM_USAGE_INGESTION_CARD.path,
		);

		if (!this.isSuperUser() || hasIngestionCard) {
			return [...items];
		}

		return [...items, CLAIM_USAGE_INGESTION_CARD];
	}
}
